import {
  Button,
  IconName,
  Link,
  Logo,
  Spacer,
  styles,
  textFieldHorizontalPadding,
  TextInput,
} from "@withorbit/ui";
import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useAuthenticationClient } from "../authentication/authContext.js";

function showSimpleAlert(text: string) {
  if (Platform.OS === "web") {
    alert(text);
  } else {
    Alert.alert(text);
  }
}

export interface SignInFormProps {
  colorPalette: styles.colors.ColorPalette;
  overrideEmailAddress?: string | null;
  onComplete: () => void;
}

export default function SignInForm({
  overrideEmailAddress,
  colorPalette,
  onComplete,
}: SignInFormProps) {
  const authenticationClient = useAuthenticationClient();

  const [email, setEmail] = useState(overrideEmailAddress ?? "");
  const [password, setPassword] = useState("");
  const [isPendingServerResponse, setPendingServerResponse] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function onResetPassword() {
    const targetEmail = overrideEmailAddress ?? email;

    if (!targetEmail) {
      showSimpleAlert("Please enter your email address first.");
      return;
    }

    try {
      await authenticationClient.sendPasswordResetEmail(targetEmail);
      showSimpleAlert(
        "We've sent you an email with instructions on how to reset your password.",
      );
    } catch (error) {
      console.error("[SignIn] Password reset failed:", error);
      showSimpleAlert(
        "We couldn't process that request. Please try again later.",
      );
    }
  }

  async function onSubmitForm() {
    if (isPendingServerResponse) {
      return;
    }

    const targetEmail = overrideEmailAddress ?? email;

    if (!targetEmail || !password) {
      setErrorMessage("Please enter both your email address and password.");
      return;
    }

    setPendingServerResponse(true);
    setErrorMessage(null);

    try {
      await authenticationClient.signInWithEmailAndPassword(
        targetEmail,
        password,
      );
      onComplete();
      return;
    } catch (error) {
      const code = getAuthErrorCode(error);

      if (code === "auth/user-not-found") {
        try {
          await authenticationClient.createUserWithEmailAndPassword(
            targetEmail,
            password,
          );
          onComplete();
          return;
        } catch (createError) {
          const createCode = getAuthErrorCode(createError);

          if (createCode === "orbit/registration-disabled") {
            setErrorMessage(
              "We couldn't sign you in. Account creation is currently restricted. If you need access, please contact the site owner.",
            );
          } else {
            setErrorMessage(getFriendlyAuthErrorMessage(createError));
          }
        }
      } else {
        setErrorMessage(getFriendlyAuthErrorMessage(error));
      }
    } finally {
      setPendingServerResponse(false);
    }
  }

  const effectiveEmail = overrideEmailAddress ?? email;

  const emailField =
    overrideEmailAddress != null ? (
      <Text
        style={[
          styles.type.label.typeStyle,
          { color: colorPalette.secondaryTextColor },
        ]}
      >
        {effectiveEmail}
      </Text>
    ) : (
      <TextInput
        style={formStyles.textInput}
        colorPalette={colorPalette}
        onChangeText={setEmail}
        value={email}
        placeholder="you@example.com"
        autoCorrect={false}
        importantForAutofill="yes"
        autoComplete="email"
        textContentType="emailAddress"
        keyboardType="email-address"
        returnKeyType="next"
      />
    );

  return (
    <View style={formStyles.container}>
      <View style={{ flex: 1 }} />
      <Logo units={3} tintColor={colorPalette.secondaryTextColor} />
      <Spacer units={8} />
      <Text style={styles.type.headline.layoutStyle}>Sign in</Text>
      <Spacer units={4} />
      {errorMessage && (
        <>
          <Text style={formStyles.errorText}>{errorMessage}</Text>
          <Spacer units={2} />
        </>
      )}
      <Text style={formStyles.label}>Email address</Text>
      <Spacer units={1} />
      {emailField}
      {Platform.OS === "web" && (
        // Help out the password managers.
        <input
          style={{ display: "none" }}
          type="text"
          name="email"
          id="email"
          autoComplete="username"
          value={effectiveEmail}
          readOnly
        />
      )}
      <Spacer units={3.5} />
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Text style={formStyles.label}>Password</Text>
        <Button
          size="tiny"
          onPress={onResetPassword}
          title="Forgot your password?"
          color={colorPalette.accentColor}
          hitSlop={40}
        />
      </View>
      <Spacer units={1} />
      <TextInput
        style={formStyles.textInput}
        colorPalette={colorPalette}
        onChangeText={setPassword}
        value={password}
        placeholder="Password"
        autoCorrect={false}
        importantForAutofill="yes"
        autoComplete="password"
        textContentType="password"
        secureTextEntry={true}
        onSubmitEditing={onSubmitForm}
        returnKeyLabel="Sign in"
        returnKeyType="done"
      />
      <Spacer units={6} />
      <View style={{ position: "relative" }}>
        <Button
          color={styles.colors.white}
          accentColor={colorPalette.accentColor}
          iconName={IconName.ArrowRight}
          title="Sign in"
          onPress={onSubmitForm}
        />
        <View style={formStyles.indicatorContainer}>
          <ActivityIndicator
            animating={isPendingServerResponse}
            color={colorPalette.accentColor}
          />
        </View>
      </View>
      <Text
        style={[
          styles.type.labelTiny.layoutStyle,
          {
            marginTop: styles.layout.gridUnit * 2,
            color: colorPalette.secondaryTextColor,
          },
        ]}
      >
        By signing in or creating an Orbit account, you confirm that you have
        read and agree to <Link href="/terms">Orbit's Terms of Service</Link>.
      </Text>
      <View style={{ flex: 1 }} />
    </View>
  );
}

function getAuthErrorCode(error: unknown): string | null {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string") {
      return code;
    }
  }
  return null;
}

function getFriendlyAuthErrorMessage(error: unknown): string {
  const code = getAuthErrorCode(error);
  switch (code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/email-already-in-use":
      return "An account with this email already exists. Please try signing in instead.";
    case "auth/wrong-password":
      return "Incorrect email or password. Please try again.";
    case "auth/too-many-requests":
      return "Too many sign-in attempts. Please wait a moment and try again.";
    case "auth/user-not-found":
      return "We couldn't sign you in with that email and password.";
    case "orbit/registration-disabled":
      return "Account creation is currently restricted. Please contact the site owner.";
    default:
      if (error instanceof Error && error.message) {
        return error.message;
      }
      return "We couldn't sign you in. Please try again.";
  }
}

const formStyles = StyleSheet.create({
  container: {
    padding: styles.layout.edgeMargin,
    width: "100%",
    maxWidth: 375,
    flex: 1,
  },

  textInput: {
    marginLeft: -textFieldHorizontalPadding,
    marginRight: -textFieldHorizontalPadding,
  },

  label: {
    ...styles.type.labelSmall.layoutStyle,
    color: styles.colors.white,
  },

  indicatorContainer: {
    position: "absolute",
    right: 0,
    bottom: styles.layout.gridUnit * 2,
  },

  errorText: {
    ...styles.type.labelSmall.layoutStyle,
    color: styles.colors.ink,
  },
});
